// Filtering, searching and paging a list without reloading the page.
//
// The server owns the markup on both paths: a normal request renders the whole
// page, and a request carrying X-Fragment renders the same results partial on
// its own (see the fragment helper in controllers/viewController.js). So the
// rows exist in exactly one place - this module fetches that partial and swaps
// it in, rather than holding a second copy of the table in JavaScript.
//
// The controls are plain GET form fields and every list URL is a plain GET the
// server renders on its own, so a filtered list stays linkable and reloadable.
// A list whose filters carry no submit button does need this module to apply
// them - see the note in docs/ARCHITECTURE.md.

// How long to wait after the last keystroke before refetching. Long enough that
// typing a name is one request rather than fifteen, short enough that the list
// still feels like it is keeping up.
const SEARCH_DEBOUNCE_MS = 350;

// Marks the request as wanting only the results partial. Anything the server
// does not recognise falls through to a full page render, so an old cached
// bundle degrades to the previous behaviour instead of breaking.
const FRAGMENT_HEADER = 'X-Fragment';

// Reads a form's fields as plain key/value pairs, dropping the empties so an
// "Alle" option leaves the query string rather than appearing as `status=`.
function formValues(form) {
  const out = {};
  new window.FormData(form).forEach((value, key) => {
    const trimmed = typeof value === 'string' ? value.trim() : value;
    if (trimmed !== '') out[key] = trimmed;
  });
  return out;
}

/**
 * Wires one list region for live updates.
 *
 * @param {object} options
 * @param {string} options.results  id of the element whose contents get replaced
 * @param {string} options.path     the list's own URL, e.g. '/kunden'
 * @param {string[]} [options.forms] ids of GET forms whose fields make the query
 * @param {function} [options.extraParams] returns extra query values per refresh,
 *   for a control that is not a form field - see the note in currentParams()
 * @returns {{refresh: function}|null} null when the region is not on this page
 */
export function initLiveList({ results, path, forms = [], extraParams = null }) {
  const region = document.getElementById(results);
  if (!region) return null;

  const formEls = forms.map((id) => document.getElementById(id)).filter(Boolean);

  // Which page to ask for. Held here rather than read back out of the DOM so a
  // filter change can reset it without having to find the pager first.
  let page = new window.URLSearchParams(window.location.search).get('page') || '';
  let inFlight = null;

  // Every control's values, merged. One place decides how they combine, so the
  // controls cannot disagree about what happens to each other's values:
  // searching keeps the filters, filtering keeps the search.
  function currentParams() {
    const params = new window.URLSearchParams();
    formEls.forEach((form) => {
      Object.entries(formValues(form)).forEach(([key, value]) => params.set(key, value));
    });

    // Values a form field cannot carry. A saved list's filter tree is the case
    // this exists for: it is a nested AND/OR structure, and `params.set`
    // collapses repeated names, so it travels as one encoded JSON parameter
    // instead. After the forms, so a list's own controls win over a stale
    // field; empties dropped here too, so an emptied tree leaves the URL rather
    // than sitting in it as filter={}.
    if (extraParams) {
      Object.entries(extraParams()).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) params.set(key, String(value));
      });
    }

    if (page && page !== '1') params.set('page', page);
    return params;
  }

  async function refresh() {
    const params = currentParams();
    const query = params.toString();
    const url = `${path}${query ? `?${query}` : ''}`;

    // The address bar tracks the list, so the result is linkable, reloadable
    // and survives the back button - replaceState rather than pushState, since
    // a filter tweak is not somewhere you want to walk back through one
    // keystroke at a time.
    window.history.replaceState({}, '', url);

    // A slow response for filters the user has already moved past must not
    // overwrite a newer one, so each request supersedes the last.
    inFlight?.abort();
    const controller = new window.AbortController();
    inFlight = controller;

    region.setAttribute('aria-busy', 'true');

    try {
      const response = await window.fetch(url, {
        headers: { [FRAGMENT_HEADER]: '1' },
        credentials: 'same-origin',
        signal: controller.signal
      });

      // A dead session redirects to /login, which arrives here as HTML that is
      // not our partial. Follow it rather than pasting a login form into the
      // table.
      if (response.redirected && new URL(response.url).pathname !== path) {
        window.location.href = response.url;
        return;
      }

      if (!response.ok) throw new Error(`Fragment request failed: ${response.status}`);

      region.innerHTML = await response.text();
    } catch (err) {
      if (err.name === 'AbortError') return;
      // Nothing is swapped in, so the list the user is looking at stays valid.
      // A full navigation is the honest fallback: it either works or shows the
      // real error page.
      window.location.href = url;
      return;
    } finally {
      if (inFlight === controller) {
        inFlight = null;
        region.removeAttribute('aria-busy');
      }
    }
  }

  // A filter or search change resets to page 1: a narrowed result set has fewer
  // pages, and staying on page 4 of 2 would show nothing.
  function refreshFromStart() {
    page = '';
    return refresh();
  }

  formEls.forEach((form) => {
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      refreshFromStart();
    });

    // Selects and checkboxes act the moment they change - a change event fires
    // for keyboard selection too, so this is not a mouse-only path. A date field
    // belongs here rather than with the text boxes: its change event fires once
    // a whole date is picked or typed, where `input` would fire on every digit.
    form
      .querySelectorAll('select, input[type="checkbox"], input[type="radio"], input[type="date"]')
      .forEach((el) => {
        el.addEventListener('change', refreshFromStart);
      });

    // Text and search boxes act on a debounce, so each keystroke does not cost
    // a request.
    form.querySelectorAll('input[type="search"], input[type="text"]').forEach((input) => {
      let timer = null;
      input.addEventListener('input', () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(refreshFromStart, SEARCH_DEBOUNCE_MS);
      });
    });
  });

  // Paging, delegated to the region so the buttons that arrive with each
  // refreshed fragment are live without rebinding anything.
  region.addEventListener('click', (event) => {
    const button = event.target.closest('[data-page]');
    if (!button || button.disabled) return;

    page = button.dataset.page;
    refresh();
  });

  // Handed back so a row action - a delete, a status toggle - can pull the list
  // again instead of reloading the page to show its result.
  return { refresh };
}
